/*
  Dbeauty · Exact search
  Shopify's storefront search ORs the words a visitor types and matches them
  against product descriptions too, so "lotion" returns every product whose
  text mentions lotion and "devana lotion" returns nine products. The
  options[fields] parameter is ignored on this store, but Shopify's query
  syntax is honoured exactly: title:devana AND title:lotion returns one
  product. This module turns what the visitor types into that syntax, in
  three places, without touching the theme's own search code:
    1. the header dropdown, by wrapping the section renderer's fetch;
    2. Enter in the header search box (the theme navigates with the raw text);
    3. every search form submit (header "View all", the results page box).

  exact():  every word must appear in the title, the product type or a tag
            (accent-insensitive, so "desirea" finds Désiréa).
  loose():  the same words joined with AND, unscoped, with prefix matching on
            the last word. Scoped words have no prefix matching, so an
            unfinished word finds nothing; the dropdown falls back to loose()
            while the visitor is still typing, and the results page does the
            same once, in place, when an exact query finds nothing.
  display(): the human words back out of a scoped query, for the search box.

  Queries that already use Shopify's own syntax (quotes, AND/OR, field:, a
  leading minus) pass through untouched. Nothing here is content: the words
  a product answers to are its title, type and tags in the admin.
*/
import { sectionRenderer } from '@theme/section-renderer';

const FIELDS = ['title', 'product_type', 'tag'];
const MAX_TERMS = 6;

const isScoped = (q) => /\btitle:/.test(q || '');
const isPowerSyntax = (q) => /["*:]|\bAND\b|\bOR\b|(^|\s)-\S/.test(q || '');

const words = (raw) =>
  (raw || '')
    .replace(/[()":*<>&]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, MAX_TERMS);

const exact = (raw) => {
  if (!raw || isPowerSyntax(raw)) return raw;
  const list = words(raw);
  if (!list.length) return raw;
  return list.map((w) => '(' + FIELDS.map((f) => `${f}:${w}`).join(' OR ') + ')').join(' AND ');
};

const loose = (raw) => {
  if (!raw || isPowerSyntax(raw)) return raw;
  const list = words(raw);
  return list.length ? list.join(' AND ') : raw;
};

const display = (q) => {
  if (!q) return '';
  if (isScoped(q)) {
    return q
      .split(' AND ')
      .map((group) => group.replace(/[()]/g, '').split(' OR ')[0].replace(/^title:/, '').trim())
      .filter(Boolean)
      .join(' ');
  }
  return q.replace(/ AND /g, ' ');
};

const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
const hasNoResults = (html) => !!parse(html).querySelector('p.predictive-search-results__no-results');

// The dropdown prints the query it ran in its "no results" line and its
// screen-reader status. Show the words, not the syntax. The scoped query holds
// no HTML-special characters (words() strips them), so plain replacement is safe.
const tidy = (html, printed, shown) => (printed && printed !== shown ? html.split(printed).join(shown) : html);

window.DbeautySearch = { exact, loose, display, isScoped, hasNoResults };

/* 1. The dropdown. */
const original = sectionRenderer.getSectionHTML.bind(sectionRenderer);
sectionRenderer.getSectionHTML = async (sectionId, useCache, url, ...rest) => {
  if (sectionId !== 'predictive-search' || !(url instanceof URL)) return original(sectionId, useCache, url, ...rest);
  const raw = (url.searchParams.get('q') || '').trim();
  if (!raw || isScoped(raw) || isPowerSyntax(raw)) return original(sectionId, useCache, url, ...rest);

  const shown = words(raw).join(' ');
  const exactQuery = exact(raw);
  const exactUrl = new URL(url);
  exactUrl.searchParams.set('q', exactQuery);
  const markup = await original(sectionId, useCache, exactUrl, ...rest);

  if (markup && hasNoResults(markup)) {
    const looseQuery = loose(raw);
    const looseUrl = new URL(url);
    looseUrl.searchParams.set('q', looseQuery);
    looseUrl.searchParams.set('resources[options][prefix]', 'last');
    const looseMarkup = await original(sectionId, useCache, looseUrl, ...rest);
    if (looseMarkup) return tidy(looseMarkup, looseQuery, shown);
  }
  return tidy(markup, exactQuery, shown);
};

/* 2. Enter in the header box: the theme reads the input and navigates with it. */
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter') return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'q' || !input.closest('predictive-search-component')) return;
    const raw = input.value.trim();
    if (!raw || isScoped(raw) || isPowerSyntax(raw)) return;
    input.value = exact(raw);
  },
  true
);

/* 3. Every search form. Capture phase: before the theme's handlers and before the browser serialises the form. */
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('role') !== 'search') return;
    const input = form.querySelector('input[name="q"]');
    if (!(input instanceof HTMLInputElement)) return;
    const raw = input.value.trim();
    if (!raw || isScoped(raw) || isPowerSyntax(raw)) return;
    input.value = exact(raw);
  },
  true
);

/* Results page: an exact query that found nothing is retried once, loosely, so an
   unfinished or slightly off word still shows something. The loose query is not
   scoped, so this cannot loop. */
const main = document.querySelector('main[data-template]');
if (main && main.dataset.template === 'search') {
  const url = new URL(location.href);
  const q = url.searchParams.get('q') || '';
  if (isScoped(q) && document.querySelector('.search-results__no-results')) {
    url.searchParams.set('q', loose(display(q)));
    url.searchParams.set('options[prefix]', 'last');
    location.replace(url.toString());
  }
}
